import { Button, Group, Loader, Menu, Table, Text } from '@mantine/core';
import { IconChevronDown, IconDots, IconDownload, IconPlus } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import ConfirmDeleteModal from 'src/common/components/ConfirmDeleteModal';
import dataCaptureClasses from 'src/data-capture/DataCapture.module.css';
import { GenericErrorPage } from 'src/error/GenericErrorPage';
import { useDataExport } from 'src/data-export/DataExportProvider';
import { useAgenda } from 'src/meeting-agenda/AgendaProvider';
import { useSuitability } from 'src/suitability-report/SuitabilityProvider';
import { useDeleteDataExport } from 'src/suitability-report/hooks/useDeleteDataExport';
import { useDeleteAgenda } from 'src/suitability-report/hooks/useDeleteAgenda';
import { useDeleteSuitabilityReport } from 'src/suitability-report/hooks/useDeleteSuitabilityReport';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import PageLoader from 'src/UI/loader/PageLoader';
import { useFileTemporaryLinkMutation } from 'src/data-capture/hooks/useFileTemporaryLinkMutation';
import FileUploadLayout from './FileUploadLayout';
import SuitabilityReportStatusPill from './SuitabilityReportStatusPill';
import { useGeneratedDocuments } from './hooks/useGeneratedDocuments';
import EmptyView from './components/EmptyView';
import { dateMatchesSearch, formatDateTime } from 'src/meetings/utils/date-utils';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { useDeleteMeetingNote } from 'src/meeting-note/hooks/useDeleteMeetingNote';
import { useMeetingNote } from 'src/meeting-note/MeetingNoteProvider';

const DOCUMENT_TYPE_LABELS = {
  'suitability-report': 'Suitability Report',
  'meeting-agenda': 'Meeting Agenda',
  'data-export': 'Data Export',
  'meeting-note': 'Meeting Note',
} as const;

export default function SiestaAIGenerated() {
  const { documents, isLoading, error, refetch } = useGeneratedDocuments();
  const { clientId } = useCurrentClient();
  const {
    createLink,
    isLoading: isCreatingLink,
    isError: creatingLinkFailed,
  } = useFileTemporaryLinkMutation();

  const {
    deleteSuitabilityReport,
    error: deleteSuitabilityReportError,
    reset: resetDeleteSuitabilityReport,
    isLoading: isDeletingSuitabilityReport,
  } = useDeleteSuitabilityReport();
  const {
    deleteMeetingAgenda,
    error: deleteAgendaError,
    reset: resetDeleteAgenda,
    isLoading: isDeletingAgenda,
  } = useDeleteAgenda();
  const {
    deleteDataExport,
    error: deleteDataExportError,
    reset: resetDeleteDataExport,
    isLoading: isDeletingDataExport,
  } = useDeleteDataExport();
  const {
    deleteMeetingNote,
    error: deleteMeetingNoteError,
    reset: resetDeleteMeetingNote,
    isLoading: isDeletingMeetingNote,
  } = useDeleteMeetingNote();

  const navigate = useNavigate();
  const { flagEnabledNewLayout } = useFlags();

  const { open: suitabilityProviderOpen } = useSuitability();
  const { open: agendaProviderOpen } = useAgenda();
  const { open: dataExportProviderOpen } = useDataExport();
  const { open: meetingNoteProviderOpen } = useMeetingNote();

  const openSuitabilityFlow = flagEnabledNewLayout
    ? () =>
        navigate(`/client/${clientId}/suitability-report`, {
          state: { fromPath: location.pathname },
        })
    : suitabilityProviderOpen;
  const openAgendaFlow = flagEnabledNewLayout
    ? () =>
        navigate(`/client/${clientId}/meeting-agenda`, { state: { fromPath: location.pathname } })
    : agendaProviderOpen;
  const openDataExportFlow = flagEnabledNewLayout
    ? () => navigate(`/client/${clientId}/data-export`, { state: { fromPath: location.pathname } })
    : dataExportProviderOpen;
  const openMeetingNote = flagEnabledNewLayout
    ? () => navigate(`/client/${clientId}/meeting-note`, { state: { fromPath: location.pathname } })
    : meetingNoteProviderOpen;

  const [searchValue, setSearchValue] = useState('');
  const [documentToDelete, setDocumentToDelete] = useState<{
    id: string;
    displayName: string;
    documentType: string;
  } | null>(null);

  const handleDeletePress = (document: {
    id: string;
    displayName: string;
    documentType: string;
  }) => {
    resetDeleteSuitabilityReport();
    resetDeleteAgenda();
    resetDeleteDataExport();
    resetDeleteMeetingNote();
    setDocumentToDelete(document);
  };

  const deleteErrorByType: Record<string, { message: string } | null> = {
    'suitability-report': deleteSuitabilityReportError,
    'meeting-agenda': deleteAgendaError,
    'data-export': deleteDataExportError,
    'meeting-note': deleteMeetingNoteError,
  };
  const deleteError = documentToDelete ? deleteErrorByType[documentToDelete.documentType] : null;

  const handleDownload = async (fileId: number) => {
    try {
      const link = await createLink(fileId);
      window.open(link.url, '_blank');
    } catch (e) {
      notifyAndLogError(
        'Failed to download document',
        'An error occurred while trying to download the document. Please try again later.',
        e,
        { clientId, fileId },
      );
      return;
    }
  };

  const handleDeleteConfirm = async () => {
    if (!documentToDelete) return;
    try {
      switch (documentToDelete.documentType) {
        case 'suitability-report':
          await deleteSuitabilityReport(clientId, documentToDelete.id);
          break;
        case 'meeting-agenda':
          await deleteMeetingAgenda(documentToDelete.id);
          break;
        case 'data-export':
          await deleteDataExport(clientId, documentToDelete.id);
          break;
        case 'meeting-note':
          await deleteMeetingNote(clientId, documentToDelete.id);
          break;
        default:
          throw new Error(`Unknown document type: ${documentToDelete.documentType}`);
      }
    } catch (e) {
      notifyAndLogError(
        'Failed to delete document',
        'An error occurred while trying to delete the document. Please try again later.',
        e,
        { clientId, documentId: documentToDelete.id, documentType: documentToDelete.documentType },
      );
    }
    setDocumentToDelete(null);
    await refetch();
  };

  const filteredDocuments = useMemo(() => {
    if (!searchValue.trim()) return documents;

    const searchLower = searchValue.toLowerCase();
    return documents.filter(
      doc =>
        doc.displayName.toLowerCase().includes(searchLower) ||
        DOCUMENT_TYPE_LABELS[doc.documentType].toLowerCase().includes(searchLower) ||
        dateMatchesSearch(doc.createdAt, searchLower),
    );
  }, [documents, searchValue]);

  const rows = useMemo(
    () =>
      filteredDocuments.map(document => (
        <Table.Tr key={`${document.documentType}-${document.id}`}>
          <Table.Td>
            <Text size="sm" fw={500}>
              {document.displayName}
            </Text>
          </Table.Td>
          <Table.Td>
            <Text size="sm">{DOCUMENT_TYPE_LABELS[document.documentType]}</Text>
          </Table.Td>
          <Table.Td>
            <Text size="sm">{formatDateTime(document.createdAt)}</Text>
          </Table.Td>
          <Table.Td>
            {document.backgroundProcess && (
              <SuitabilityReportStatusPill status={document.backgroundProcess.status} />
            )}
          </Table.Td>
          <Table.Td align="right">
            <Group justify="space-between" wrap="nowrap">
              {document.fileId ? (
                <Button
                  variant="primary"
                  onClick={() => handleDownload(document.fileId!)}
                  rightSection={<IconDownload size={16} />}
                >
                  Download
                </Button>
              ) : (
                <div />
              )}
              <Menu
                transitionProps={{ transition: 'pop-top-right' }}
                position="bottom-end"
                width={220}
                withinPortal
              >
                <Menu.Target>
                  <IconDots color="#475467" stroke={2} size={20} />
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item onClick={() => handleDeletePress(document)}>Delete</Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Group>
          </Table.Td>
        </Table.Tr>
      )),
    [filteredDocuments, handleDownload, setDocumentToDelete],
  );

  if (error || creatingLinkFailed) {
    return <GenericErrorPage error={error || new Error('Failed to create download link')} />;
  }

  if (isCreatingLink) {
    return <PageLoader absolute={false} />;
  }

  if (filteredDocuments.length === 0 && !isLoading) {
    return (
      <FileUploadLayout title="Siesta AI generated">
        <EmptyView
          title="Your AI-generated documents will appear here"
          subtitle="Generate suitability reports, meeting agendas, and data exports with Siesta AI. All your AI-created documents will be organized in one convenient location."
          customCta={
            <GenerateMenu
              openSuitabilityFlow={openSuitabilityFlow}
              openAgendaFlow={openAgendaFlow}
              openDataExportFlow={openDataExportFlow}
              openMeetingNote={openMeetingNote}
              buttonText="Generate new"
            />
          }
        />
      </FileUploadLayout>
    );
  }

  return (
    <FileUploadLayout
      title="Siesta AI generated"
      showSearch
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      actions={
        <GenerateMenu
          openSuitabilityFlow={openSuitabilityFlow}
          openAgendaFlow={openAgendaFlow}
          openDataExportFlow={openDataExportFlow}
          openMeetingNote={openMeetingNote}
        />
      }
    >
      <Table
        variant="bare"
        captionSide="bottom"
        classNames={{ th: dataCaptureClasses.th }}
        verticalSpacing={'sm'}
      >
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Document Name</Table.Th>
            <Table.Th>Document Type</Table.Th>
            <Table.Th>Created</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th></Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {isLoading ? (
            <Table.Tr>
              <Table.Td colSpan={5} style={{ textAlign: 'center', padding: '2rem' }}>
                <Loader size="sm" />
              </Table.Td>
            </Table.Tr>
          ) : rows.length > 0 ? (
            rows
          ) : (
            <Table.Tr>
              <Table.Td colSpan={5} style={{ textAlign: 'center', padding: '2rem' }}>
                <Text c="dimmed">
                  {searchValue.trim()
                    ? 'No documents match your search'
                    : 'No generated documents found'}
                </Text>
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>

      <ConfirmDeleteModal
        opened={!!documentToDelete}
        onClose={() => setDocumentToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete document"
        message={`Are you sure you want to delete "${documentToDelete?.displayName}"? This action cannot be undone.`}
        error={deleteError}
        isLoading={
          isDeletingSuitabilityReport ||
          isDeletingAgenda ||
          isDeletingDataExport ||
          isDeletingMeetingNote
        }
      />
    </FileUploadLayout>
  );
}

type GenerateMenuProps = {
  openSuitabilityFlow: () => void;
  openAgendaFlow: () => void;
  openDataExportFlow: () => void;
  openMeetingNote: () => void;
  buttonText?: string;
};

function GenerateMenu({
  openSuitabilityFlow,
  openAgendaFlow,
  openDataExportFlow,
  openMeetingNote,
  buttonText = 'Generate',
}: GenerateMenuProps) {
  return (
    <Menu position="bottom-end" withinPortal>
      <Menu.Target>
        <Button
          variant="primary"
          leftSection={<IconPlus size={16} />}
          rightSection={<IconChevronDown size={16} />}
          size="md"
        >
          {buttonText}
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item onClick={openSuitabilityFlow}>Suitability Report</Menu.Item>
        <Menu.Item onClick={openAgendaFlow}>Meeting Agenda</Menu.Item>
        <Menu.Item onClick={openDataExportFlow}>Data Export</Menu.Item>
        <Menu.Item onClick={openMeetingNote}>Meeting Note</Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}
